// src/footballAdmin/leagueSchedulerThunks.js
import moment from 'moment-timezone';
import { runScheduleWorker } from './runScheduleWorker'
import { createSchedulePreview } from './createSchedulePreview'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'
import { setLeagueSchedulerState } from '@severed-links/common.redherrings-reducers/footballAdmin';

// Helper: build scheduleToSave and progress
function buildSchedulePayload(grid) {
  const scheduleToSave = grid.map(g => ({
    teamId: g._id,
    teamName: g.teamName,
    opponents: g.games.map(x => x.opp?._id ?? null),
  }));
  const totalSlots = grid.length * (grid[0]?.games?.length ?? 0);
  const filled = grid.reduce((acc, r) => acc + r.games.filter(x => !!x.opp).length, 0);
  const progress = totalSlots ? Math.round((100 * filled) / totalSlots) : 0;
  return { scheduleToSave, progress };
}

// Generate a preview off the main thread; saving remains a separate action.
export const runLeagueSchedulerNow = (seasonId, { seed = 42, signal } = {}) => async (dispatch, getState) => {
  const state = getState();
  const sched = state.footballAdmin.leagueSchedulers.find(i => i.seasonId === seasonId) || {};
  const { seasonName, teams = [], weeks = [], divisions = [], divisionNames = [], WAIT_MS = 100 } = sched;

  if (!teams.length || !weeks.length) {
    dispatch(setLeagueSchedulerState(seasonId, { isRunning: false }));
    return;
  }

  // mark start
  const startTime = moment().toISOString();
  dispatch(setLeagueSchedulerState(seasonId, {
    startTime,
    isRunning: true,
    isDone: false,
    progress: 0,
    passes: 0,
    resets: 0,
    scheduleToSave: null,
  }));

  try {
    const result = await runScheduleWorker({ teams, weeks, seed }, signal)
    if (!result || signal?.aborted) return
    const { ok, grid } = result
    if (!ok) throw new Error('No complete schedule was found. Review the team divisions and season weeks, then try again.')

    // Attach extra fields your UI expects (divisionName, numGames)
    const divisionNameFromCode = (divCode) => {
      const idx = divisions.findIndex(d => d === divCode);
      return divisionNames[idx] || divCode;
    };

    const hydrated = grid.map(row => ({
      ...row,
      divisionName: divisionNameFromCode(row.division),
      numGames: row.games.filter(g => !!g.opp).length,
    }));

    // Generation is complete; briefly reveal the preview one week at a time.
    const interval = Number.isFinite(WAIT_MS) && WAIT_MS > 0 ? WAIT_MS : 100
    dispatch(setLeagueSchedulerState(seasonId, { grid: createSchedulePreview(hydrated, 0) }))
    for (let completedWeeks = 1; completedWeeks <= weeks.length; completedWeeks++) {
      await new Promise(resolve => {
        const finish = () => {
          clearTimeout(timer)
          signal?.removeEventListener('abort', finish)
          resolve()
        }
        const timer = setTimeout(finish, interval)
        signal?.addEventListener('abort', finish, { once: true })
      })
      if (signal?.aborted) return
      if (completedWeeks < weeks.length) {
        dispatch(setLeagueSchedulerState(seasonId, {
          grid: createSchedulePreview(hydrated, completedWeeks),
          progress: Math.round(100 * completedWeeks / weeks.length),
          gridUpdated: moment().toISOString(),
        }))
      }
    }

    const { scheduleToSave, progress } = buildSchedulePayload(hydrated);

    dispatch(setLeagueSchedulerState(seasonId, {
      grid: hydrated,
      scheduleToSave,
      isDone: ok,
      isRunning: false,
      progress: ok ? 100 : progress,
      gridUpdated: moment().toISOString(),
    }));
  } catch (error) {
    if (signal?.aborted) return
    dispatch(setLeagueSchedulerState(seasonId, {
      isRunning: false,
      isDone: false,
      scheduleToSave: null,
    }))
    dispatch(createNotification({
      type: 'error',
      headline: 'League Scheduler',
      message: error.message || 'Unable to generate the league schedule. Please try again.',
      timeout: 10000,
    }))
  }
};
